import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import type { ManagedRole } from '@/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { ACCESS_LEVELS, type AccessLevel, PROTECTED_WORKFLOW_PERMISSIONS, normalizeRole } from '@/lib/permissions';
import { cn } from '@/lib/utils';

const schema = z.object({
  name: z.string().trim().min(2, 'Role name is required').max(64),
  description: z.string().trim().max(300).optional(),
  permissions: z.array(z.string()).min(1, 'Select at least one permission'),
  isActive: z.boolean(),
});
type FormData = z.infer<typeof schema>;

export default function RoleFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { user, hasPermission, hasAnyRole } = useAuth();
  const isSystemAdmin = hasAnyRole(['ORG_ADMIN', 'Admin']);
  const actorPermissions = user?.permissions ?? [];

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '', permissions: [], isActive: true },
  });
  const selected = watch('permissions') ?? [];

  const { data: role, isLoading: loadingRole } = useQuery({
    queryKey: ['managed-role', id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: ManagedRole }>(`/roles/${id}`);
      return response.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (role) reset({ name: role.name, description: role.description ?? '', permissions: role.permissionIds ?? [], isActive: true });
  }, [role, reset]);

  const modules = useMemo(() => Object.entries(ACCESS_LEVELS), []);

  const isCanonical = !!role?.isSystem;
  const isProtectedRole = normalizeRole(role?.name ?? '') === 'ORG_ADMIN';
  const canManageCustom = isEdit ? hasPermission('roles:update') : hasPermission('roles:create');
  const readOnly = isProtectedRole || (isCanonical ? !isSystemAdmin : !canManageCustom);
  const isProtected = (key: string) => (PROTECTED_WORKFLOW_PERMISSIONS as readonly string[]).includes(key);
  // Workflow approval permissions stay fixed (non-grantable) on canonical roles,
  // but may be granted to custom roles by an actor who already holds them (or a
  // System Admin). Mirrors the backend: you cannot grant permissions you do not hold.
  const canGrant = (key: string) => {
    if (isProtected(key) && isCanonical) return false;
    return isSystemAdmin || actorPermissions.includes(key);
  };

  const canGrantLevel = (level: AccessLevel) => level.permissions.every((key) => canGrant(key));

  const isLevelSelected = (level: AccessLevel) => {
    const grantable = level.permissions.filter((key) => canGrant(key));
    return grantable.length > 0 && grantable.every((key) => selected.includes(key));
  };

  const isLevelPartial = (level: AccessLevel) => {
    const grantable = level.permissions.filter((key) => canGrant(key));
    return grantable.some((key) => selected.includes(key)) && !isLevelSelected(level);
  };

  const LEVEL_APPROVE = 'Approve';
  const LEVEL_VIEW = 'View';

  const toggleLevel = (level: AccessLevel, moduleLevels: AccessLevel[]) => {
    if (readOnly || !canGrantLevel(level)) return;
    const grantable = level.permissions.filter((key) => canGrant(key));
    const turningOn = !grantable.every((key) => selected.includes(key));
    let next = turningOn
      ? [...new Set([...selected, ...grantable])]
      : selected.filter((key) => !grantable.includes(key));
    // Selecting Approve also selects that module's View level so the approver can
    // see the records; Manage is deliberately not auto-selected.
    if (turningOn && level.label === LEVEL_APPROVE) {
      const viewLevel = moduleLevels.find((l) => l.label === LEVEL_VIEW);
      if (viewLevel) {
        const viewKeys = viewLevel.permissions.filter((key) => canGrant(key));
        next = [...new Set([...next, ...viewKeys])];
      }
    }
    setValue('permissions', next, { shouldValidate: true, shouldDirty: true });
  };

  const toggleModule = (levels: AccessLevel[]) => {
    if (readOnly) return;
    const grantableKeys = levels.flatMap((l) => l.permissions.filter((key) => canGrant(key)));
    const allSelected = grantableKeys.length > 0 && grantableKeys.every((key) => selected.includes(key));
    setValue('permissions', allSelected
      ? selected.filter((key) => !grantableKeys.includes(key))
      : [...new Set([...selected, ...grantableKeys])], { shouldValidate: true, shouldDirty: true });
  };

  const mutation = useMutation({
    mutationFn: async (values: FormData) => {
      if (!isEdit) return api.post('/roles', values);
      if (!role) return;
      if (role.isSystem) {
        await api.put(`/roles/${role.id}`, { description: values.description || null, isActive: values.isActive });
      } else {
        await api.put(`/roles/${role.id}`, { name: values.name, description: values.description || null, isActive: values.isActive });
      }
      await api.put(`/roles/${role.id}/permissions`, { permissions: values.permissions });
    },
    onSuccess: () => {
      addToast(`Role ${isEdit ? 'updated' : 'created'} successfully.`, 'success');
      queryClient.invalidateQueries({ queryKey: ['managed-roles'] });
      queryClient.invalidateQueries({ queryKey: ['assignable-roles'] });
      navigate('/admin/roles');
    },
    onError: (error: any) => addToast(error.message || 'Unable to save role.', 'error'),
  });

  if (isEdit && loadingRole) return <PageLoading />;

  const totalGrantable = modules.reduce((sum, [, levels]) => sum + levels.reduce((s, l) => s + l.permissions.filter((key) => canGrant(key)).length, 0), 0);

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-1">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-heading font-bold text-primary">{isEdit ? 'Edit Role' : 'Create Role'}</h1>
          <p className="text-xs text-secondary mt-0.5">
            {isProtectedRole
              ? 'The ORG_ADMIN role is protected. It cannot be edited, renamed, deactivated or deleted.'
              : isCanonical
                ? 'Edit the description and non-mandatory permissions for this canonical role. Its name and approval workflow permissions are fixed.'
                : 'Choose exactly what users assigned to this role can access.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="space-y-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="grid gap-3 p-4 grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="space-y-1 min-w-0">
              <Label htmlFor="role-name">Role Name *</Label>
              <Input id="role-name" {...register('name')} disabled={readOnly || isCanonical} className={cn('h-9', errors.name && 'border-error')} />
              <FormError message={errors.name?.message} />
              {isProtectedRole
                ? <p className="text-[11px] text-secondary">The ORG_ADMIN role is protected and cannot be renamed.</p>
                : isCanonical && <p className="text-[11px] text-secondary">Canonical role names are used by the application and cannot be renamed.</p>}
            </div>
            <div className="space-y-1 min-w-0">
              <Label htmlFor="role-description">Description</Label>
              <Textarea id="role-description" {...register('description')} disabled={readOnly} rows={2} className={cn('min-h-9 resize-none', errors.description && 'border-error')} />
              <FormError message={errors.description?.message} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-2">
                <h2 className="text-sm font-semibold text-primary">Access Permissions</h2>
                <p className="text-xs text-secondary">{selected.length} of {totalGrantable} selected</p>
              </div>
              {!readOnly && <Button type="button" variant="outline" size="sm" onClick={() => setValue('permissions', selected.filter((key) => isProtected(key)), { shouldValidate: true })}>Clear all</Button>}
            </div>

            <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {modules.map(([module, levels]) => {
                const grantableKeys = levels.flatMap((l) => l.permissions.filter((key) => canGrant(key)));
                const allSelected = grantableKeys.length > 0 && grantableKeys.every((key) => selected.includes(key));
                return (
                  <section key={module} className="rounded-lg border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-primary">{module}</h3>
                      {!readOnly && grantableKeys.length > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleModule(levels)}
                          className="shrink-0 text-[11px] font-medium text-blue-600 hover:underline"
                        >
                          {allSelected ? 'Clear' : 'Select all'}
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {levels.map((level) => {
                        const disabled = readOnly || !canGrantLevel(level);
                        return (
                          <label
                            key={level.label}
                            className={cn(
                              'flex items-center gap-2 rounded border px-2 py-1.5',
                              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-surface-secondary'
                            )}
                            style={{ borderColor: 'var(--border-subtle)' }}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 shrink-0 rounded accent-blue-600"
                              checked={isLevelSelected(level)}
                              disabled={disabled}
                              ref={(el) => { if (el) el.indeterminate = isLevelPartial(level); }}
                              onChange={() => toggleLevel(level, levels)}
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-[13px] font-medium leading-tight text-primary">{level.label}</span>
                              <span className="block truncate text-[11px] leading-tight text-secondary">{level.description}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
            <FormError message={errors.permissions?.message} />
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
          <Button type="button" variant="outline" className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/admin/roles')}>Back</Button>
          {!readOnly && <Button type="submit" loading={mutation.isPending} className="w-full sm:w-auto min-h-[44px]">{isEdit ? 'Save Changes' : 'Create Role'}</Button>}
        </div>
      </form>
    </div>
  );
}
